import React, { useEffect, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { Box } from "@mui/material"
import { useMutationPostClassesTransactionsBanker } from "api/reactQuery/mutations/classesTransactions"
import Dialog from "components/common/dialog/dialog"
import { useClassId } from "hooks/navigation"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import {
  ISalaryTransactionApiCreate,
  ITransactionApiCreate,
} from "ts/interfaces/Transaction"
import { TransactionType } from "ts/types/Transaction"
import { getErrorMessageFromTab } from "utils/api"
import { handleKeyDown } from "utils/keydown"
import { checkTransaction } from "utils/sound"

import { selectSound } from "../../../../store/utility/utility.selectors"
import Transactions from "../../../form/common/formPart/transactions"
import {
  transactionsDefaultValue,
  transactionsSchema,
} from "../../../form/common/formPart/transactions/Transactions.config"
import {
  getCombinedTransactions,
  getIsNoTransactionAdded,
} from "../../../form/common/formPart/transactions/Transactions.utils"

interface DialogSendTransactionProps {
  handleClose: () => void
  isOpen: boolean
  defaultExpandedSections?: TransactionType[]
  markedStudentId: number[]
  isToggleAll?: boolean
  excludedStudentId: number[]
  onSuccessPostClassesTransactions?: () => void
}

const DialogSendTransaction = ({
  handleClose,
  isOpen,
  defaultExpandedSections,
  markedStudentId,
  isToggleAll,
  excludedStudentId,
  onSuccessPostClassesTransactions,
}: DialogSendTransactionProps) => {
  const { t } = useTranslation()
  const methods = useForm({
    resolver: yupResolver(transactionsSchema),
    defaultValues: transactionsDefaultValue,
  })

  const classId = useClassId()
  const { showSnackbar } = useCustomSnackbar()
  const soundChecked = useAppSelector(selectSound)
  const [transactionType, setTransactionType] = useState<
    (ITransactionApiCreate | ISalaryTransactionApiCreate)[]
  >([])

  const queryClient = useQueryClient()

  useEffect(() => {
    checkTransaction(transactionType, soundChecked)
  }, [transactionType])

  const { mutate, isLoading } = useMutationPostClassesTransactionsBanker({
    options: {
      onSuccess: (_, variables) => {
        setTransactionType(variables.data.transactions)

        showSnackbar({
          title: "transactions have been sent successfully",
        })
        queryClient.invalidateQueries(["bankerClass"])
        methods.reset()
        handleClose()
        onSuccessPostClassesTransactions && onSuccessPostClassesTransactions()
      },
      onError: (error) => {
        showSnackbar({
          title: getErrorMessageFromTab(error),
          variant: "error",
        })
      },
    },
  })

  const handleActionButtonClick = () => {
    const data = methods.getValues()

    if (getIsNoTransactionAdded(data)) {
      showSnackbar({
        title: t("students.selectAtLeaseOneTransaction"),
        variant: "info",
      })
      return
    }

    const allTransactions = getCombinedTransactions(data)
    if (allTransactions.length > 20) {
      showSnackbar({
        title: t("students.selectUpTo20Transactions"),
        variant: "info",
      })
      return
    }

    methods.handleSubmit(() => onSubmit(allTransactions))()
  }

  const onSubmit = (
    allTransactions: (ITransactionApiCreate | ISalaryTransactionApiCreate)[]
  ) => {
    if (isToggleAll) {
      mutate({
        classId: Number(classId),
        data: {
          send_to_whole_class: isToggleAll,
          excluded_student_ids: excludedStudentId,
          transactions: allTransactions,
        },
      })
    } else {
      mutate({
        classId: Number(classId),
        data: {
          send_to_whole_class: isToggleAll,
          student_ids: markedStudentId,
          transactions: allTransactions,
        },
      })
    }
  }

  return (
    <Dialog
      titleText={t("students.sendTransaction")}
      onActionButtonClick={handleActionButtonClick}
      isLoading={isLoading}
      open={isOpen}
      onClose={handleClose}
      desktopMaxWidth="1040px"
      tabletMaxWidth="720px"
      actionAcceptButtonProps={{ disabled: isLoading }}
    >
      <Box width="100%" component="form">
        <FormProvider {...methods}>
          <Transactions
            onClickForm={(e) =>
              handleKeyDown(e, handleActionButtonClick, isLoading)
            }
            classId={Number(classId)}
            expandedSections={defaultExpandedSections}
          />
        </FormProvider>
      </Box>
    </Dialog>
  )
}
export default DialogSendTransaction
